import React, {
    useCallback, useState, useContext, useEffect
} from 'react';
import htmlParser from 'html-react-parser';
import sanitizeHtml from 'sanitize-html-react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
    Accordion,
    AccordionCard,
    Button,
    Link
} from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext } from '@jutro/locale';
import CommonConstants from 'sg-common-constants';

import messages from './SgHelpDialog.messages';

const chatTime = {
    startTime: moment('8:30:00', 'hh:mm:ss'),
    endTime: moment('17:00:00', 'hh:mm:ss')
};
const Saturday = 6;
const Sunday = 0;

const topicsMotor = [
    {
        id: 'topic1',
        question: 'MotorFaqQuestion1',
        answer: 'MotorFaqAnswer1'
    },
    {
        id: 'topic2',
        question: 'MotorFaqQuestion2',
        answer: 'MotorFaqAnswer2'
    },
    {
        id: 'topic3',
        question: 'MotorFaqQuestion3',
        answer: '' // custom answer in html
    }
];
const topicsHome = [
    {
        id: 'topic4',
        question: 'HomeFaqQuestion1',
        answer: 'HomeFaqAnswer1'
    },
    {
        id: 'topic5',
        question: 'HomeFaqQuestion2',
        answer: 'HomeFaqAnswer2'
    },
    {
        id: 'topic6',
        question: 'HomeFaqQuestion3',
        answer: 'HomeFaqAnswer3'
    }
];

function SgHelpDialog(props) {
    const {
        showDialog,
        closeDialog
    } = props;

    const history = useHistory();
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const homeContactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_HOME_CHEQUE_PHONE_NUMBER;
    const motorContactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_MOTOR_CHEQUE_PHONE_NUMBER;

    const [openedAccordion, setOpenedAccordion] = useState('faq');

    useEffect(() => {
        if (showDialog) {
            const helpDialog = document.getElementsByClassName('sg-help-dialog-container')[0];
            const firstFAQ = helpDialog.querySelectorAll('*[tabindex="0"]')[0];
            if (firstFAQ) {
                firstFAQ.focus();
            }
        }
    }, [showDialog]);

    const goToFAQPage = useCallback(() => {
        history.push('/faq', {});
        closeDialog();
    }, [closeDialog, history]);

    const isOpen = useCallback((accordionName) => {
        return accordionName === openedAccordion;
    }, [openedAccordion]);

    const isChatAvailable = () => {
        return moment().day() === Saturday || moment().day() === Sunday
            ? false
            : moment().isBetween(chatTime.startTime, chatTime.endTime);
    };

    const getAnswer = (topic) => {
        switch (topic.id) {
            case 'topic3':
                return (
                    <div>
                        <p>{htmlParser(sanitizeHtml(translator(messages.MotorFaqAnswer3_1)))}</p>
                        <p>{translator(messages.MotorFaqAnswer3_2)}</p>
                        <ul>
                            <li>{translator(messages.MotorFaqAnswer3_3)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_4)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_5)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_6)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_7)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_8)}</li>
                            <li>{translator(messages.MotorFaqAnswer3_9)}</li>
                        </ul>
                        <p>{translator(messages.MotorFaqAnswer3_10)}</p>
                        <p>{translator(messages.MotorFaqAnswer3_11)}</p>
                        <p>{translator(messages.MotorFaqAnswer3_12)}</p>
                        <p>{htmlParser(sanitizeHtml(translator(messages.MotorFaqAnswer3_13)))}</p>
                    </div>
                );
            case 'topic6':
                return (
                    <p>
                        {
                            translator(messages.HomeFaqAnswer3, {
                                documentsLink: (
                                    <Link to="/documents">
                                        {translator(messages.Documents)}
                                    </Link>
                                ),
                                homePhoneNumber: isMobile
                                    ? (
                                        <a href={`tel:${CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_HOME_PHONE_NUMBER.replaceAll(' ', '')}`}>
                                            {CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_HOME_PHONE_NUMBER}
                                        </a>
                                    ) : CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_HOME_PHONE_NUMBER,
                                motorPhoneNumber: isMobile
                                    ? (
                                        <a href={`tel:${CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER2.replaceAll(' ', '')}`}>
                                            {CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER2}
                                        </a>
                                    ) : CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_MOTOR_PHONE_NUMBER2,
                            })
                        }
                    </p>
                );
            default:
                return (
                    <p>{translator(messages[topic.answer])}</p>
                );
        }
    };

    const drawTopics = (topics, product) => {
        return topics.map((topic, index) => {
            return (
                <AccordionCard
                    key={topic.id}
                    id={`${product}FAQ${index}`}
                    isCollapsible
                    title={translator(messages[topic.question])}
                >
                    {getAnswer(topic)}
                </AccordionCard>
            );
        });
    };

    if (showDialog) {
        return (
            <div className="sg-help-dialog-container">
                <div
                    className="sg-dialog-screen-background"
                    onClick={closeDialog}
                    role="button"
                    tabIndex="-1"
                    onKeyUp={closeDialog}
                />
                <div className="sg-help-dialog-popup-container">
                    <div className="helpDialogDesktop">
                        <div className="sg-dialog-top-arrow" />
                        <div className="accordion accordionFAQ">
                            <div className="accordionHead">
                                <div className="helpLabelDialog">
                                    <h2>{translator(messages.FaqHeader)}</h2>
                                </div>
                                {!isOpen('faq')
                                    && (
                                        <Button
                                            onClick={() => setOpenedAccordion('faq')}
                                            className="sgButton sgPrimaryButton helpButtonDialog"
                                        >
                                            <span>{translator(messages.FaqButton)}</span>
                                        </Button>
                                    )
                                }

                            </div>
                            {isOpen('faq')
                                && (
                                    <div className="accordionDesc">
                                        <div>
                                            <Accordion closeOthers disabled={false} id="FaqAccordion" showFrame={false}>
                                                <div className="faqs-container faqs-motor">
                                                    {drawTopics(topicsMotor, 'motor')}
                                                </div>
                                                <div className="faqs-container faqs-home">
                                                    {drawTopics(topicsHome, 'home')}
                                                </div>
                                            </Accordion>
                                        </div>
                                        {/* todo: do not show on wizard */}
                                        <Button
                                            id="moreFAQs"
                                            className="sgButton sgPrimaryButton helpButtonDialog"
                                            onClick={goToFAQPage}
                                        >
                                            <i className="sgFAQ" aria-hidden="true" />
                                            {translator(messages.MoreFaqsButton)}
                                        </Button>
                                    </div>
                                )
                            }
                            <hr />
                        </div>

                        <div className="accordion">
                            <div className="accordionHead">
                                <div className="helpLabelDialog">
                                    <h2>{isChatAvailable() ? translator(messages.ChatWithUs) : translator(messages.LeaveMessage)}</h2>
                                    {!isChatAvailable()
                                        && <p>{translator(messages.ConsultantWillContactYou)}</p>
                                    }
                                </div>
                                <Button id="chatMessageBtn" className="saga-intercom sgButton sgPrimaryButton helpButtonDialog">
                                    <i className="sgChat" aria-hidden="true" />
                                    {isChatAvailable() ? translator(messages.ChatNow) : translator(messages.MessageUs)}
                                </Button>
                            </div>
                            <hr />
                        </div>

                        <div className="accordion accordionCallUs">
                            <div className="accordionHead">
                                <div className="helpLabelDialog">
                                    <h2>{translator(messages.TalkToTeamMember)}</h2>
                                    <p>{translator(messages.OurLinesAreOpen)}</p>
                                    <p>{translator(messages.OpenWeekdays)}</p>
                                    <p>{translator(messages.OpenSaturday)}</p>
                                    <p>{translator(messages.OpenSunday)}</p>
                                </div>
                                <div>
                                    {!isOpen('accordionCallUs')
                                        && (
                                            <Button
                                                id="callUsBtn"
                                                className="sgButton sgPrimaryButton helpButtonDialog"
                                                onClick={() => setOpenedAccordion('accordionCallUs')}
                                            >
                                                <i className="sgCallUs" aria-hidden="true" />
                                                {translator(messages.CallUs)}
                                            </Button>
                                        )
                                    }
                                </div>
                            </div>
                            {isOpen('accordionCallUs')
                                && (
                                    <div className="accordionDesc">
                                        <h3>{translator(messages.CarInsurance)}</h3>
                                        <p className="sg-phone-number-text">{motorContactUsPhoneNumber}</p>

                                        <h3>{translator(messages.HomeInsurance)}</h3>
                                        <p className="sg-phone-number-text">{homeContactUsPhoneNumber}</p>
                                    </div>
                                )
                            }
                            <hr />
                        </div>

                        <div className="sg-modal-footer">
                            <Button
                                id="closeBtn"
                                className="sgButton sgSecondaryBlueButton helpButtonDialog"
                                onClick={closeDialog}
                            >
                                {translator(messages.Close)}
                            </Button>
                        </div>
                    </div>

                    {/* Mobile */}
                    <div className="helpDialogMobile">
                        <div className="sg-help-dialog-header">
                            <h2>
                                {isOpen('faq') ? translator(messages.PopularFaqs) : translator(messages.Help)}
                                <Button className="closeButtonCross" onClick={closeDialog}>
                                    <span>{translator(messages.Close)}</span>
                                </Button>
                            </h2>
                        </div>
                        <div className="accordion accordionFAQ">
                            <div className="accordionHead">
                                {!isOpen('faq')
                                    && (
                                        <Button
                                            onClick={() => setOpenedAccordion('faq')}
                                            className="sgButton sgPrimaryButton helpButtonDialog"
                                        >
                                            {translator(messages.FaqButton)}
                                        </Button>
                                    )
                                }
                            </div>
                            {isOpen('faq')
                                && (
                                    <div className="accordionDesc">
                                        <div>
                                            <Accordion
                                                closeOthers
                                                disabled={false}
                                                id="FaqAccordion"
                                                showFrame={false}
                                            >
                                                <div className="faqs-container faqs-motor">
                                                    {drawTopics(topicsMotor, 'motor')}
                                                </div>
                                                <div className="faqs-container faqs-home">
                                                    {drawTopics(topicsHome, 'home')}
                                                </div>
                                            </Accordion>
                                        </div>
                                        {/* todo: do not show on wizard */}
                                        <Button
                                            id="moreFAQs"
                                            className="sgButton sgPrimaryButton helpButtonDialog"
                                            onClick={goToFAQPage}
                                        >
                                            <i className="sgFAQ" aria-hidden="true" />
                                            {translator(messages.MoreFaqsButton)}
                                        </Button>
                                    </div>
                                )}
                        </div>
                        {!isOpen('accordionCallUs')
                            && (
                                <div className="sg-help-dialog-call-us-section">
                                    <h2>{translator(messages.NeedToContactUs)}</h2>
                                </div>
                            )
                        }
                        <div className="accordion">
                            <Button className="sgButton sgPrimaryButton helpButtonDialog">
                                <i className="sgChat" aria-hidden="true" />
                                {translator(isChatAvailable() ? messages.ChatOpenMobile : messages.MessageUsMobile)}
                            </Button>
                        </div>
                        <div className="accordion accordionCallUs">
                            {!isOpen('accordionCallUs')
                                && (
                                    <Button
                                        className="sgButton sgPrimaryButton helpButtonDialog"
                                        onClick={() => setOpenedAccordion('accordionCallUs')}
                                    >
                                        <i className="sgCallUs" aria-hidden="true" />
                                        {translator(messages.CallUs)}
                                    </Button>
                                )
                            }
                            {isOpen('accordionCallUs')
                                && (
                                    <div className="accordionDesc">
                                        <div className="helpLabelDialog">
                                            <h2>{translator(messages.TalkToTeamMember)}</h2>
                                            <p>{translator(messages.OurLinesAreOpen)}</p>
                                            <p>{translator(messages.OpenWeekdays)}</p>
                                            <p>{translator(messages.OpenSaturday)}</p>
                                            <p>{translator(messages.OpenSunday)}</p>
                                        </div>
                                        <div>
                                            <h3>{translator(messages.HomeInsurance)}</h3>
                                            <Link
                                                className="sgButton sgPrimaryButton helpButtonDialog"
                                                href={`tel:${homeContactUsPhoneNumber.replaceAll(' ', '')}`}
                                            >
                                                <i className="sgCallUs" aria-hidden="true" />
                                                {homeContactUsPhoneNumber}
                                            </Link>
                                            <h3>{translator(messages.CarInsurance)}</h3>
                                            <Link
                                                className="sgButton sgPrimaryButton helpButtonDialog"
                                                href={`tel:${motorContactUsPhoneNumber.replaceAll(' ', '')}`}
                                            >
                                                <i className="sgCallUs" aria-hidden="true" />
                                                {motorContactUsPhoneNumber}
                                            </Link>
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return '';
}

export default SgHelpDialog;
